/**
 * Real-time Menu Synchronization Hook
 * 
 * Connects to the WebSocket endpoint to receive instant updates
 * when menu items, add-ons, modifiers, or categories are changed
 * in the admin panel.
 * 
 * Falls back gracefully when WebSocket is unavailable (e.g., due to 
 * proxy/ingress limitations in preview environments).
 * 
 * Usage:
 *   const { isConnected, lastUpdate } = useMenuSync(onMenuUpdate);
 * 
 * The onMenuUpdate callback is called whenever a menu change is detected.
 * Components should refetch their menu data in this callback.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

// Get WebSocket URL from backend URL
const getWsUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Convert http(s):// to ws(s)://
  return backendUrl.replace(/^http/, 'ws') + '/api/ws/menu';
};

// Check if we're in a preview/development environment where WS might not work
const isPreviewEnvironment = () => {
  const url = window.location.hostname;
  return url.includes('preview.emergentagent.com') || url.includes('localhost');
};

export const useMenuSync = (onMenuUpdate) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  // Reduce max attempts in preview environments where WS likely won't work
  const MAX_RECONNECT_ATTEMPTS = isPreviewEnvironment() ? 3 : 10;
  const RECONNECT_DELAY_BASE = 2000; // Start with 2 seconds

  // Stable callback ref to avoid reconnections on callback changes
  const onMenuUpdateRef = useRef(onMenuUpdate);
  useEffect(() => {
    onMenuUpdateRef.current = onMenuUpdate;
  }, [onMenuUpdate]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    // Don't spam console in preview environments after initial attempt
    if (reconnectAttemptsRef.current > 0 && isPreviewEnvironment()) {
      // Silent reconnect attempt
    }

    try {
      const wsUrl = getWsUrl();
      
      // Only log on first attempt
      if (reconnectAttemptsRef.current === 0) {
        console.log('[MenuSync] Connecting to:', wsUrl);
      }
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[MenuSync] Connected successfully');
        setIsConnected(true);
        setConnectionError(null);
        setUsingFallback(false);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'ping') {
            // Respond to server ping
            ws.send(JSON.stringify({ type: 'pong' }));
            return;
          }
          
          if (data.type === 'connected') {
            console.log('[MenuSync] Server confirmed connection:', data.message);
            return;
          }
          
          if (data.type === 'menu_update') {
            console.log('[MenuSync] Menu update received:', data.update_type, data.data);
            setLastUpdate({
              type: data.update_type,
              data: data.data,
              timestamp: data.timestamp || new Date().toISOString()
            });
            
            // Call the callback to trigger menu refetch
            if (onMenuUpdateRef.current) {
              onMenuUpdateRef.current(data);
            }
          }
        } catch (e) {
          // Handle non-JSON messages (like 'pong')
          if (event.data === 'pong') {
            return;
          }
          console.warn('[MenuSync] Failed to parse message:', e);
        }
      };

      ws.onclose = (event) => {
        // Only log closure details in non-preview environments or on first disconnect
        if (!isPreviewEnvironment() || reconnectAttemptsRef.current === 0) {
          console.log('[MenuSync] Disconnected:', event.code, event.reason || 'No reason provided');
        }
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = RECONNECT_DELAY_BASE * Math.pow(1.5, reconnectAttemptsRef.current);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else {
          // In preview environments, gracefully fall back without showing error
          if (isPreviewEnvironment()) {
            console.log('[MenuSync] WebSocket unavailable in preview environment. Menu will sync on page refresh.');
            setUsingFallback(true);
            setConnectionError(null); // Don't show error to user
          } else {
            console.log('[MenuSync] Max reconnect attempts reached');
            setConnectionError('Real-time menu sync unavailable. Menu will refresh on page load.');
          }
        }
      };

      ws.onerror = () => {
        // Don't log error in preview environments (expected behavior due to proxy)
        if (!isPreviewEnvironment()) {
          console.warn('[MenuSync] WebSocket connection error');
        }
        // Note: onerror is always followed by onclose, so we handle reconnection there
      };

    } catch (error) {
      if (!isPreviewEnvironment()) {
        console.error('[MenuSync] Failed to create WebSocket:', error);
      }
      setConnectionError('Failed to connect');
    }
  }, []);

  // Request a manual refresh from the server
  const requestRefresh = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'request_refresh' }));
    }
  }, []);

  // Connect on mount
  useEffect(() => {
    connect();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
    };
  }, [connect]);

  // Send keepalive ping every 25 seconds (only when connected)
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 25000);

    return () => clearInterval(pingInterval);
  }, []);

  return {
    isConnected,
    lastUpdate,
    connectionError,
    requestRefresh,
    usingFallback  // True when WS is unavailable and we've given up reconnecting
  };
};

export default useMenuSync;
